import plugLockup from '@assets/brand/plug-lockup-white.svg'
import { AccountAvatar } from '@modules/AccountList'
import { PLUG_DOCS_URL, PLUG_SUPPORT_URL } from '@plug-core/constants/urls'
import { lock } from '@plug-core/handlers/walletActions/wallet'
import useWallet from '@plug-core/hooks/useWallet'
import { useConnectionProvider } from '@plug-core/state'
import { useMobileSidebar } from '@providers/MobileSidebarProvider'
import { Button, cn, Dialog, Text } from '@ui'
import { disconnect } from '@utils/disconnect'
import { ROUTES } from '@utils/routes'
import React, { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  HiBookOpen,
  HiCurrencyDollar,
  HiLockClosed,
  HiSupport,
  HiTrendingUp,
  HiUserGroup,
} from 'react-icons/hi'
import { LuHistory } from 'react-icons/lu'
import { MdCollections } from 'react-icons/md'
import { PiSignOutBold } from 'react-icons/pi'
import { TbMapSearch } from 'react-icons/tb'
import { VscSettings } from 'react-icons/vsc'
import { useLocation, useNavigate } from 'react-router-dom'

const BOTTOM_ITEMS = [
  { icon: HiBookOpen, label: 'Documentation', url: PLUG_DOCS_URL },
  { icon: HiSupport, label: 'Support', url: PLUG_SUPPORT_URL },
]

export const SIDEBAR_WIDTH = 280

const itemBase =
  'flex w-full cursor-pointer items-center gap-3 rounded-md border-0 bg-transparent px-4 text-left font-text no-underline transition-colors duration-150 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-focus'

interface SidebarProps {
  /** Rendered inside the mobile Sheet: fills the sheet and drops its own border. */
  inSheet?: boolean
}

export const Sidebar: FC<SidebarProps> = ({ inSheet }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { wallet } = useWallet()
  const { close } = useMobileSidebar()
  const { t } = useTranslation()
  const { provider } = useConnectionProvider()

  const [showLogoutPrompt, setShowLogoutPrompt] = useState(false)

  const hideLock = provider === 'oisy' || provider === 'plug'
  const navItems = [
    { icon: HiCurrencyDollar, label: 'Home', route: ROUTES.HOME },
    { icon: MdCollections, label: 'NFTs', route: ROUTES.NFT_OVERVIEW },
    { icon: LuHistory, label: 'Activity', route: ROUTES.USER_ACTIVITY },
    { icon: HiTrendingUp, label: 'DeFi Hub', route: ROUTES.DEFI_HUB },
    { icon: HiUserGroup, label: 'Contacts', route: ROUTES.CONTACTS },
    { icon: TbMapSearch, label: 'Discover', route: ROUTES.DISCOVER },
    { icon: VscSettings, label: 'Settings', route: ROUTES.SETTINGS },
    { icon: HiLockClosed, label: 'Lock', onClick: lock, hide: hideLock },
    { icon: PiSignOutBold, label: 'Sign out', onClick: () => setShowLogoutPrompt(true) },
  ]

  return (
    <nav
      aria-label="Main"
      className={cn(
        'flex h-full shrink-0 flex-col px-4 pb-6',
        inSheet ? 'w-full' : 'border-r border-hairline'
      )}
      style={inSheet ? undefined : { width: SIDEBAR_WIDTH, minWidth: SIDEBAR_WIDTH }}
    >
      <div className="px-4 pt-7 pb-5">
        <img src={plugLockup} alt="Plug" draggable={false} className="block h-7 w-auto" />
      </div>

      <button
        type="button"
        onClick={() => {
          navigate(ROUTES.ACCOUNTS)
          close()
        }}
        className={cn(itemBase, 'mb-4 py-3 text-fg hover:bg-surface-2')}
      >
        <AccountAvatar avatar={wallet.plugbook?.profilePic} size={42} background="mid" />

        <span className="flex min-w-0 flex-col justify-center">
          <Text as="span" variant="body" weight="semibold" truncate className="text-[18px]">
            {wallet.plugbook?.name ? '@' + wallet.plugbook.name : wallet.name || 'Account'}
          </Text>

          {wallet.plugbook?.name && (
            <Text as="span" variant="body-s" tone="fg-2" truncate>
              {wallet.name || 'Account'}
            </Text>
          )}
        </span>
      </button>

      <ul className="m-0 flex w-full flex-1 list-none flex-col gap-1 p-0">
        {navItems.map(({ icon: Icon, label, route, onClick, hide }) => {
          if (hide) return

          const selected = route ? location.pathname.startsWith(route) : false

          return (
            <li key={label}>
              <button
                type="button"
                aria-current={selected ? 'page' : undefined}
                onClick={() => {
                  if (onClick) {
                    onClick()
                  } else {
                    navigate(route)
                    close()
                  }
                }}
                className={cn(
                  itemBase,
                  'py-3 text-[15px] leading-[1.3] font-semibold',
                  selected ? 'bg-surface-2 text-fg' : 'text-fg-2 hover:bg-surface-2 hover:text-fg'
                )}
              >
                <Icon size={22} aria-hidden="true" className="shrink-0" />
                {label}
              </button>
            </li>
          )
        })}
      </ul>

      <ul className="m-0 flex w-full list-none flex-col gap-0.5 p-0">
        {BOTTOM_ITEMS.map(({ icon: Icon, label, url }) => (
          <li key={label}>
            <button
              type="button"
              onClick={() => window.open(url, '_blank')}
              className={cn(
                itemBase,
                'py-2 text-[14px] leading-[1.4] font-medium text-fg-3 hover:bg-surface-2 hover:text-fg-2'
              )}
            >
              <Icon size={18} aria-hidden="true" className="shrink-0" />
              {label}
            </button>
          </li>
        ))}
      </ul>

      <Dialog
        open={showLogoutPrompt}
        onClose={() => setShowLogoutPrompt(false)}
        description="Make sure you have access to your initial authentication method, or you might loose access to your assets!"
        actions={
          <>
            <Button variant="danger" fullWidth onClick={disconnect}>
              {t('common.continue')}
            </Button>
            <Button variant="secondary" fullWidth onClick={() => setShowLogoutPrompt(false)}>
              {t('common.cancel')}
            </Button>
          </>
        }
      />
    </nav>
  )
}

export default Sidebar
